import { ThemeProvider } from "@emotion/react"
import styled from "@emotion/styled"
import { Box, mq, theme } from "@pallet-hq/hegel"
import { ChangePartnershipTierModal } from "@pallet/features/PartnershipType/ChangePartnershipTierModal"
import { ChangePartnershipTierModalProvider } from "@pallet/features/PartnershipType/ChangePartnershipTierModal/hooks"
import { BillingCycleSelector } from "@pallet/features/PartnershipTierType/BillingCycleSelector"
import {
    BillingCycleSelectProvider,
    useBillingCycleSelect,
} from "@pallet/features/PartnershipTierType/BillingCycleSelector/hook"
import { PartnershipTierSelectorItem } from "../PartnershipTierSelectorItem"
import { PartnershipTierSelectorFragment } from "__generated__/PartnershipTierSelectorFragment"
import { ContingencyTierSelectorItem } from "../PartnershipTierSelectorItem/variants/Contingency"

type PartnershipTierSelectorProps = {
    applylist: PartnershipTierSelectorFragment
    recruitingTeam?: string
    referrer?: "jobs" | "talent"
}

const PartnershipTierSelectComponent = (
    props: PartnershipTierSelectorProps
) => {
    const { showBillingCycle } = useBillingCycleSelect()
    const { partnershipTiers, showContingencyTier } = props.applylist

    if (!partnershipTiers?.totalCount) return null

    return (
        <>
            {showBillingCycle(partnershipTiers.edges[0].node) && (
                <BillingCycleSelector />
            )}
            <Box mb="xlarge" />
            <Wrapper>
                <CardRow
                    numTiers={
                        +showContingencyTier + partnershipTiers.totalCount
                    }
                >
                    {partnershipTiers.edges.map((tier, index) => (
                        <PartnershipTierSelectorItem
                            key={index}
                            partnershipTier={tier.node}
                            {...props}
                        />
                    ))}
                    {showContingencyTier && (
                        <ContingencyTierSelectorItem {...props} />
                    )}
                </CardRow>
            </Wrapper>
        </>
    )
}

const Wrapper = styled.div`
    overflow-x: auto;
    width: 100%;
`

const CardRow = styled.div<{ numTiers: number }>`
    display: grid;
    ${({ numTiers }) =>
        mq({
            gridTemplateColumns: ["1fr", `repeat(${numTiers}, 304px)`],
        })};
    grid-column-gap: ${theme.space.medium};
    width: fit-content;
    margin: 0 auto;
`

export const PartnershipTierSelector = (
    props: PartnershipTierSelectorProps
) => (
    <ThemeProvider
        theme={{
            ...theme,
            colors: {
                ...theme.colors,
                primary: props.applylist.primaryColor || theme.colors.primary,
            },
        }}
    >
        <ChangePartnershipTierModalProvider
            recruitingTeam={props.recruitingTeam}
        >
            <BillingCycleSelectProvider>
                <PartnershipTierSelectComponent {...props} />
                <ChangePartnershipTierModal />
            </BillingCycleSelectProvider>
        </ChangePartnershipTierModalProvider>
    </ThemeProvider>
)
